/**
 * Distinct PDF templates per document type (Phase 18 batch H).
 * Includes a graphic, semi-transparent rotated approval stamp that DOES NOT
 * cover content — placed in the top-right corner over a small-margin area.
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const FOOTER_NOTE = 'Generated by FM Flexi Pro™ · SKG Apps';

function header(doc, { title, company }) {
  // Top brand strip
  doc.setFillColor(245, 158, 11);
  doc.rect(0, 0, 210, 8, 'F');

  // Logo placeholder + company block (left)
  if (company?.logo_url) {
    try { doc.addImage(company.logo_url, 'PNG', 14, 14, 22, 22); } catch (_) { /* ignore */ }
  }
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold').setFontSize(14);
  doc.text(company?.name || 'FM Flexi Pro', 40, 22);
  doc.setFont('helvetica', 'normal').setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(company?.address || '', 40, 27);
  doc.text([company?.phone, company?.email].filter(Boolean).join(' · '), 40, 32);
  doc.text(`TRN: ${company?.trn || '—'}`, 40, 37);

  // Title (right)
  doc.setTextColor(180, 83, 9);
  doc.setFont('helvetica', 'bold').setFontSize(20);
  doc.text(title, 196, 22, { align: 'right' });
}

function approvalStamp(doc, name, role, date) {
  if (!name) return;
  // Place at top-right corner, rotated 12°, semi-transparent.
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.45 }));
  doc.setDrawColor(34, 139, 34);
  doc.setLineWidth(1.2);
  // Rounded rect rotated
  const cx = 168, cy = 50;
  doc.setFont('helvetica', 'bold').setFontSize(11);
  doc.setTextColor(34, 139, 34);
  // Manual rotation by transforming text — jsPDF supports angle option
  doc.text('APPROVED', cx, cy, { angle: 12, align: 'center' });
  doc.setFontSize(7).setFont('helvetica', 'normal');
  doc.text(`${name}`, cx, cy + 4.5, { angle: 12, align: 'center' });
  doc.text(`${role || ''}`, cx, cy + 8, { angle: 12, align: 'center' });
  doc.text(`${date}`, cx, cy + 11.5, { angle: 12, align: 'center' });
  // Outer rectangle, also rotated approximately
  doc.setLineWidth(0.6);
  doc.rect(cx - 18, cy - 5, 36, 19);
  doc.restoreGraphicsState();
}

function footer(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    doc.setFontSize(8).setTextColor(148, 163, 184);
    doc.text(FOOTER_NOTE, 14, h - 8);
    doc.text(`Page ${i} of ${pageCount}`, 196, h - 8, { align: 'right' });
  }
}

export function generateQuotationPDF(q, { company, partyName }) {
  const doc = new jsPDF();
  const isPurchase = q.direction === 'purchase';
  header(doc, { title: isPurchase ? 'PURCHASE QUOTATION' : 'SALES QUOTATION', company });

  // Meta block (right under title)
  doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(15, 23, 42);
  let y = 50;
  doc.text(`Quote #: ${q.quotation_no}`, 196, y, { align: 'right' }); y += 4.5;
  doc.text(`Date: ${q.issue_date?.split('T')[0] || '—'}`, 196, y, { align: 'right' }); y += 4.5;
  doc.text(`Valid until: ${q.valid_until?.split('T')[0] || '—'}`, 196, y, { align: 'right' });

  // Party block
  doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(15, 23, 42);
  doc.text(isPurchase ? 'From supplier' : 'To client', 14, 50);
  doc.setFont('helvetica', 'normal').setFontSize(9);
  doc.text(partyName || '—', 14, 55);
  if (q.contact_name) doc.text(`Contact: ${q.contact_name}`, 14, 60);
  if (q.contact_email) doc.text(q.contact_email, 14, 65);
  if (q.contact_phone) doc.text(q.contact_phone, 14, 70);

  // Title / scope
  doc.setFont('helvetica', 'bold').setFontSize(11);
  doc.text(q.title || '', 14, 80);
  if (q.scope_summary) {
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(71, 85, 105);
    const lines = doc.splitTextToSize(q.scope_summary, 180);
    doc.text(lines, 14, 85);
  }

  // Line items
  const body = (q.line_items || []).map((li, i) => {
    const gross = (li.quantity || 0) * (li.unit_price || 0);
    const disc = gross * ((li.discount_pct || 0) / 100);
    const taxable = gross - disc;
    const tax = taxable * ((li.tax_pct || 0) / 100);
    return [
      i + 1,
      `${li.item_name || ''}${li.description ? `\n${li.description}` : ''}`,
      `${li.quantity || 0} ${li.unit || ''}`,
      (li.unit_price || 0).toFixed(2),
      `${li.discount_pct || 0}%`,
      `${li.tax_pct || 0}%`,
      (taxable + tax).toFixed(2),
    ];
  });
  doc.autoTable({
    startY: 100,
    head: [['#', 'Description', 'Qty', 'Rate', 'Disc', 'Tax', 'Total']],
    body,
    theme: 'striped',
    headStyles: { fillColor: [180, 83, 9], textColor: 255 },
    columnStyles: { 0: { cellWidth: 8 }, 6: { halign: 'right' } },
    styles: { fontSize: 9 },
  });

  let endY = doc.lastAutoTable.finalY + 6;
  // Totals box
  doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(15, 23, 42);
  doc.text(`Subtotal: ${(q.subtotal || 0).toFixed(2)}`, 196, endY, { align: 'right' }); endY += 5;
  doc.text(`Discount: -${(q.discount_total || 0).toFixed(2)}`, 196, endY, { align: 'right' }); endY += 5;
  doc.text(`Tax (VAT): ${(q.tax_total || 0).toFixed(2)}`, 196, endY, { align: 'right' }); endY += 5;
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(180, 83, 9);
  doc.text(`Grand Total: ${(q.grand_total || 0).toFixed(2)} ${q.currency || 'AED'}`, 196, endY, { align: 'right' });
  endY += 10;

  // Terms
  if (q.terms) {
    doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(15, 23, 42);
    doc.text('Terms & Conditions', 14, endY); endY += 4;
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(71, 85, 105);
    const tlines = doc.splitTextToSize(q.terms, 180);
    doc.text(tlines, 14, endY);
    endY += tlines.length * 3.5;
  }

  // Sign-off block
  endY = Math.max(endY + 10, 240);
  doc.setDrawColor(200);
  doc.line(14, endY, 80, endY);
  doc.line(120, endY, 196, endY);
  doc.setFontSize(8).setTextColor(71, 85, 105);
  doc.text('Prepared by', 14, endY + 4);
  doc.text(q.prepared_by_name || '—', 14, endY + 9);
  doc.text('Approved by', 120, endY + 4);
  doc.text(q.approved_by_name || '—', 120, endY + 9);
  if (q.approved_at) doc.text(q.approved_at.split('T')[0], 120, endY + 13);

  // Approval stamp (only when status is approved/invoiced)
  if (['approved', 'invoiced'].includes(q.status)) {
    approvalStamp(doc, q.approved_by_name, 'Manager', q.approved_at?.split('T')[0] || '');
  }

  footer(doc);
  doc.save(`${q.quotation_no || 'quotation'}.pdf`);
}

export function generateVoucherPDF(v, { company }) {
  const doc = new jsPDF();
  const isReceipt = v.voucher_type === 'receipt';
  header(doc, { title: isReceipt ? 'RECEIPT VOUCHER' : 'PAYMENT VOUCHER', company });

  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(15, 23, 42);
  let y = 50;
  doc.text(`Voucher #: ${v.voucher_no}`, 196, y, { align: 'right' }); y += 4.5;
  doc.text(`Date: ${v.date?.split('T')[0] || '—'}`, 196, y, { align: 'right' }); y += 4.5;
  doc.text(`Mode: ${v.mode}`, 196, y, { align: 'right' });

  doc.setFont('helvetica', 'bold').setFontSize(10);
  doc.text(isReceipt ? 'Received from' : 'Paid to', 14, 50);
  doc.setFont('helvetica', 'normal').setFontSize(11);
  doc.text(v.party_name, 14, 56);

  // Amount box
  doc.setFillColor(252, 211, 77);
  doc.roundedRect(14, 75, 182, 24, 3, 3, 'F');
  doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(120, 53, 15);
  doc.text(`${(v.amount || 0).toFixed(2)} ${v.currency || 'AED'}`, 105, 92, { align: 'center' });
  doc.setFontSize(9).setFont('helvetica', 'normal');
  doc.text('Amount', 105, 80, { align: 'center' });

  // Details
  let dy = 110;
  doc.setFontSize(9).setTextColor(15, 23, 42);
  if (v.cheque_number) { doc.text(`Cheque #: ${v.cheque_number}`, 14, dy); dy += 5; }
  if (v.bank_name) { doc.text(`Bank: ${v.bank_name}`, 14, dy); dy += 5; }
  if (v.reference_number) { doc.text(`Reference: ${v.reference_number}`, 14, dy); dy += 5; }
  if (v.against_invoice_id) { doc.text(`Against Invoice: ${v.against_invoice_id}`, 14, dy); dy += 5; }
  if (v.against_lpo_id) { doc.text(`Against LPO: ${v.against_lpo_id}`, 14, dy); dy += 5; }
  if (v.narration) {
    doc.setFont('helvetica', 'bold'); doc.text('Narration', 14, dy + 5); dy += 9;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(v.narration, 180);
    doc.text(lines, 14, dy);
    dy += lines.length * 4;
  }

  // Sign-off
  const endY = 240;
  doc.setDrawColor(200);
  doc.line(14, endY, 80, endY);
  doc.line(120, endY, 196, endY);
  doc.setFontSize(8).setTextColor(71, 85, 105);
  doc.text('Prepared by', 14, endY + 4);
  doc.text(v.prepared_by_name || '—', 14, endY + 9);
  doc.text('Approved by', 120, endY + 4);
  doc.text(v.approved_by_name || '—', 120, endY + 9);
  if (v.approved_at) doc.text(v.approved_at.split('T')[0], 120, endY + 13);

  if (['approved', 'paid'].includes(v.status)) {
    approvalStamp(doc, v.approved_by_name, 'Manager', v.approved_at?.split('T')[0] || '');
  }

  footer(doc);
  doc.save(`${v.voucher_no || 'voucher'}.pdf`);
}
